import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Users, X, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const GestionUTEs = () => {
  const [utes, setUtes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    nombre: "",
    cuit: "",
    cuenta_contable_id: null,
    miembros: [{ proveedor_id: "", porcentaje: "" }]
  });

  const fetchUtes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/utes`);
      setUtes(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchProveedores = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores`);
      setProveedores(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchUtes(), fetchProveedores()]);
      setLoading(false);
    };
    load();
  }, []);

  const addMiembro = () => {
    setForm({ ...form, miembros: [...form.miembros, { proveedor_id: "", porcentaje: "" }] });
  };

  const removeMiembro = (index) => {
    if (form.miembros.length <= 1) return;
    setForm({ ...form, miembros: form.miembros.filter((_, i) => i !== index) });
  };

  const updateMiembro = (index, field, value) => {
    const newMiembros = [...form.miembros];
    newMiembros[index][field] = value;
    setForm({ ...form, miembros: newMiembros });
  };

  const totalPorcentaje = form.miembros.reduce((sum, m) => sum + (parseFloat(m.porcentaje) || 0), 0);

  const handleOpenNew = () => {
    setEditando(null);
    setForm({ nombre: "", cuit: "", cuenta_contable_id: null, miembros: [{ proveedor_id: "", porcentaje: "" }] });
    setModalOpen(true);
  };

  const handleEdit = (ute) => {
    setEditando(ute);
    setForm({
      nombre: ute.nombre,
      cuit: ute.cuit || "",
      cuenta_contable_id: ute.cuenta_contable_id,
      miembros: ute.miembros.map(m => ({
        proveedor_id: m.proveedor_id.toString(),
        porcentaje: m.porcentaje.toString()
      }))
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre) { toast.error("Ingrese el nombre de la UTE"); return; }
    if (form.miembros.length < 2) { toast.error("Una UTE debe tener al menos 2 miembros"); return; }
    if (Math.abs(totalPorcentaje - 100) > 0.01) { toast.error("Los porcentajes deben sumar 100%"); return; }

    const invalidMiembro = form.miembros.find(m => !m.proveedor_id || !m.porcentaje);
    if (invalidMiembro) { toast.error("Complete proveedor y porcentaje de todos los miembros"); return; }

    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        cuit: form.cuit || null,
        cuenta_contable_id: form.cuenta_contable_id,
        miembros: form.miembros.map(m => ({
          proveedor_id: parseInt(m.proveedor_id),
          porcentaje: parseFloat(m.porcentaje)
        }))
      };

      if (editando) {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/utes/${editando.id}`, payload);
        toast.success("UTE actualizada");
      } else {
        await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/utes`, payload);
        toast.success("UTE creada exitosamente");
      }
      setModalOpen(false);
      fetchUtes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta UTE?")) return;
    await axios.delete(`${API_URL}/api/erp/empresas/${TENANT_ID}/utes/${id}`);
    toast.success("UTE eliminada");
    fetchUtes();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-utes">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">UTEs - Uniones Transitorias</h1>
          <p className="text-gray-600">Gestion de Uniones Transitorias de Empresas para pagos agrupados</p>
        </div>
        <Button onClick={handleOpenNew} data-testid="btn-nueva-ute">
          <Plus className="w-4 h-4 mr-2" />Nueva UTE
        </Button>
      </div>

      {utes.length === 0 ? (
        <Card className="cao-card">
          <CardContent className="text-center py-16">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 text-lg">No hay UTEs registradas</p>
            <p className="text-sm text-gray-500 mt-2">
              Las UTEs permiten agrupar proveedores y distribuir pagos automaticamente segun porcentajes
            </p>
          </CardContent>
        </Card>
      ) : (
        utes.map(ute => (
          <Card key={ute.id} className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2853B9]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#2853B9]" />
                </div>
                <div>
                  <CardTitle className="text-lg text-[#1E3876]">{ute.nombre}</CardTitle>
                  {ute.cuit && <p className="text-sm text-gray-500">CUIT: {ute.cuit}</p>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(ute)}>
                  <Edit2 className="w-4 h-4 mr-1" />Editar
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(ute.id)}>
                  <Trash2 className="w-4 h-4 mr-1" />Eliminar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead className="text-right">Porcentaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ute.miembros?.map((m, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{m.razon_social}</TableCell>
                      <TableCell className="font-mono text-sm">{m.proveedor_cuit || "-"}</TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-[#2853B9]/10 text-[#2853B9]">{parseFloat(m.porcentaje).toFixed(1)}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      {/* Modal Crear/Editar UTE */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">{editando ? "Editar UTE" : "Nueva UTE"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la UTE *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: UTE Obra Hospital" />
              </div>
              <div>
                <Label>CUIT</Label>
                <Input value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} placeholder="30-12345678-9" />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Miembros de la UTE</Label>
                <div className="flex items-center gap-2">
                  <span className={"text-sm font-semibold " + (Math.abs(totalPorcentaje - 100) > 0.01 ? "text-red-600" : "text-green-600")}>
                    Total: {totalPorcentaje.toFixed(1)}%
                  </span>
                  <Button variant="outline" size="sm" onClick={addMiembro}>
                    <Plus className="w-4 h-4 mr-1" />Miembro
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {form.miembros.map((miembro, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1">
                      {i === 0 && <Label className="text-xs">Proveedor</Label>}
                      <Select value={miembro.proveedor_id} onValueChange={(v) => updateMiembro(i, "proveedor_id", v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                        <SelectContent>
                          {proveedores.map(p => (
                            <SelectItem key={p.id} value={p.id.toString()}>{p.razon_social} ({p.cuit})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      {i === 0 && <Label className="text-xs">%</Label>}
                      <Input type="number" step="0.1" min="0" max="100" value={miembro.porcentaje}
                        onChange={(e) => updateMiembro(i, "porcentaje", e.target.value)} placeholder="%" />
                    </div>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={() => removeMiembro(i)} disabled={form.miembros.length <= 1}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || Math.abs(totalPorcentaje - 100) > 0.01}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUTEs;
